/* global API */
import { nextTick } from 'vue';
import { useSelectKeyword } from './keywords';
import { useGetTitle } from './data';

/**
 * Tarkistetaan onko kieli valittu ja varmistetaan että vähintään yksi on valittu
 */
export function useCheckLanguages() {
	const that = this; // this talteen looppia varten

	if (!this.formData.langs.fi.active && !this.formData.langs.sv.active && !this.formData.langs.en.active) {
		// jos ainuttakaan kieltä ei ole valittu, valitaan suomi
		this.formData.langs.fi.active = true;
		this.$refs.tabs.selectTab('#fi');
	} else {
		// tabs -komponentti ei osaa tarkistaa onko tab active ja disabled samaan aikaan
		// joten tarkistetaan se tässä, loopataan tabit läpi
		this.$refs.tabs?.tabs?.forEach((tab) => {
			if (tab.isDisabled && tab.isActive) {
				// loopataan kaikki kielet läpi ja otetaan ensimmäinen aktiviinen kieli,
				// ja valitaan se tabiksi. järjestys on suomi > ruotsi > englanti

				const langs = new Map(Object.entries(that.formData.langs));

				langs.forEach((lang, index) => {
					if (lang.active) {
						that.$refs.tabs.selectTab(`#${index}`);
					}
				});
			}
		});
	}
}

/**
 * Materiaalinen suosikiksi lisääminen tai poistaminen
 * @param {object} mat Materiaali
 */
export function useFavorite(mat) {
	const material = mat;

	const body = {
		post_id: material.post_id,
		post_type: 'materiaalit',
	};

	if (material.favorited) {
		// Jos materiaali on suosikeissa

		fetch(`${API.url}/user/remove_favorite`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
				'X-WP-Nonce': API.nonce,
			},
			body: JSON.stringify(body),
		})
			.then((res) => res.json())
			.then((response) => {
				if (response.status === 200) {
					// Asetetaan false koska materiaali oli jo suosikeissa (poistetaan suosikeista)
					material.favorited = false;

					if (!material.favorited_users_count || material.favorited_users_count <= 1) {
						// Poistetaan frontista numero jos counttia ei ole tai tämän jälkeen count olisi
						// yksi tai alle
						material.favorited_users_count = '';
					} else {
						material.favorited_users_count -= 1;
					}
				}
			})
			.catch((error) => console.error('Error', error));
	} else {
		// Jos materiaali ei ole suosikeissa

		fetch(`${API.url}/user/favorite`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
				'X-WP-Nonce': API.nonce,
			},
			body: JSON.stringify(body),
		})
			.then((res) => res.json())
			.then((response) => {
				if (response.status === 200) {
					// Asetetaan true koska materiaali ei ole suosikeissa (lisätään suosikkeihin)
					material.favorited = true;

					if (!material.favorited_users_count) {
						// Jos counttia ei ollut, voidaan olettaa sen olleen 0, joten asetetaan se yhteen
						material.favorited_users_count = 1;
					} else {
						// Lisätään counttiin
						material.favorited_users_count += 1;
					}
				}
			})
			.catch((error) => console.error('Error', error));
	}
}

/**
 * Validate the form fields
 * @returns {boolean} True if form is valid, false if not
 */
export async function useValidateFields() {
	const isValid = await this.v$.$validate();

	if (isValid) {
		return true;
	}

	/**
	 * Array forms, jonka sisällä on array form
	 * Ensimmäisen arrayn index on sama kuin formin index - 1
	 * Eli jos formeja on kolme, niin tähän forms arrayn sisällä tulee olla kolme arrayta,
	 * samassa järjestyksessä kuin formit ovat.
	 *
	 * esim. Formeja on materiaalin perustiedot, materiaalin sisällöt ja materiaalin käyttö
	 * Array 0 on materiaalin perustiedot
	 * Array 1 on materiaalin sisällöt
	 * Array 2 on materiaalin käyttö
	 */
	const { forms } = this;

	// Tutkitaan missä formeissa on virheitä
	const errors = forms.some((form, index) => {
		const formIndex = Number(index) + 1;

		return form.some((slug) =>
			this.v$.formData[slug].$errors.some(async (error) => {
				this.activeForm = formIndex;

				await nextTick();

				if (slug === 'langs') {
					const { fi, sv, en } = this.v$.formData[slug];

					if (fi.$error) {
						this.$refs.tabs.selectTab('#fi');
					} else if (sv.$error) {
						this.$refs.tabs.selectTab('#sv');
					} else if (en.$error) {
						this.$refs.tabs.selectTab('#en');
					}
				}

				await nextTick();

				const element = document.getElementById(error.$propertyPath);
				const elementPos = element.getBoundingClientRect().top;
				const offset = 150;
				const scrollPos = elementPos + window.pageYOffset - offset;

				window.scrollTo({
					top: scrollPos,
					behavior: 'smooth',
				});

				return true;
			}),
		);
	});

	if (errors) {
		return false;
	}

	return true;
}

/**
 * Piilottaa hakusanojen ehdotukset kun klikataan pois määritetyltä
 * alueelta
 */
export function useOnClickAway() {
	this.helpers.showKeywords = false;
}

/**
 * Piilottaa julkaisijatahojen ehdotukset kun klikataan pois määritetyltä * alueelta
 */
export function useOnPublisherClickAway() {
	this.helpers.showPublishers = false;
}

/**
 * Julkaisijatahon lisääminen
 */
export function useAddPublisher() {
	if (this.helpers.showPublishers) {
		const selected = useSelectKeyword();

		if (selected) {
			// Jos jotain on valittu, parsitaan se ja asetetaan publisheriksi, sekä piilotetaan ehdotukset
			const data = JSON.parse(selected);

			this.helpers.publisher = data.post_title;
			this.helpers.publisherData = data;
			this.helpers.showPublishers = false;
		} else {
			// Mitään ei ole valittu, piilota ehdotukset eikä lisää mitään
			this.helpers.showPublishers = false;
		}
	} else if (this.helpers.publisherData && this.helpers.publisherData.ID) {
		// Jos ehdotukset eivät ole näkyvissä, mutta publisherData on todella valittu
		this.formData.julkaisijatahot.push(this.helpers.publisherData);
		this.helpers.publisher = '';
		this.helpers.publisherData = {};
	}
}

/**
 * Julkaisijatahojen haku
 */
export function useGetPublishers() {
	fetch(`${API.url}/publishers/all`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json; charset=utf-8',
			'X-WP-Nonce': API.nonce,
		},
	})
		.then((res) => res.json())
		.then((response) => {
			this.data.publishers = response;
		})
		.catch((error) => console.error(error));
}

/**
 *
 */
export function useGetUserPublishers() {
	fetch(`${API.url}/user/organisations`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json; charset=utf-8',
			'X-WP-Nonce': API.nonce,
		},
	})
		.then((res) => res.json())
		.then((response) => {
			this.results.user_publishers = response;

			const that = this;

			this.results?.user_publishers?.results?.sort((a, b) => {
				const aTitle = useGetTitle(a, that);
				const bTitle = useGetTitle(b, that);

				return aTitle.localeCompare(bTitle);
			});
		})
		.catch((error) => console.error('Error', error));
}

/**
 * Checks if the publisher is member of the LYKE-network
 * @returns {boolean} True if publisher is member of the LYKE-network, false if not
 */
export function usePublisherLykeStatus() {
	if (!this.results?.user_publishers?.results) {
		return false;
	}

	const publisher = this.results.user_publishers.results.find(
		(pub) => Number(pub.post_id) === Number(this.formData.publisher),
	);

	if (publisher?.lyke_status) {
		return true;
	}

	return false;
}

/**
 * Valitsee publisherin ja piilottaa listan
 * @param {string} publisher Valittu publisher
 */
export function usePublisherClick(publisher) {
	this.helpers.publisher = publisher.post_title;
	this.helpers.publisherData = publisher;
	this.helpers.showPublishers = false;
}

/**
 * Tarkistetaan ja varmistetaan että kohderyhmät ovat oikein
 * @param {*} event Checkboxin event
 * @param  {string} selector Form datan selector
 */
export function useCheckTargetGroups(event, selector) {
	if (event.target.value === 'kaikille-sopiva') {
		/* Jos target_group arrayssa ei ole yhtään arvoa (eli kaikille sopiva oli jo valittuna tai mitään ei ollut valittuna), lisätään sinne kaikille sopiva materiaali. Jos arrayssa on asioita ja checkboxin value on kaikille sopiva materiaali, poistetaan arraysta kaikki muut arvot. */

		if (this.formData[selector].length === 0) {
			this.formData[selector].push('kaikille-sopiva');
		} else {
			this.formData[selector] = this.formData[selector].filter((value) => value === 'kaikille-sopiva');
		}
	} else {
		/* Jos event.target.value on jotain muuta kuin kaikille sopiva, poistetaan arraysta kaikille sopiva materiaali. */
		this.formData[selector] = this.formData[selector].filter((value) => value !== 'kaikille-sopiva');
	}
}

/**
 * Kielen lisääminen
 */
export function useAddLanguage() {
	const lang = JSON.parse(this.helpers.language.replaceAll("'", '"'));

	this.formData.kielet.push(lang);
}

/**
 * Poistaa avainsanat arraysta
 * @param {number} index Avainsanan index arrayssa
 */
export function useRemoveLanguage(index) {
	this.formData.kielet.splice(index, 1);
}

/**
 * Poistaa avainsanat arraysta
 * @param {number} index Avainsanan index arrayssa
 */
export function useRemovePublisher(index) {
	this.formData.julkaisijatahot.splice(index, 1);
}
